import { useState } from "react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { Card } from "../components/Card";
import { Button } from "../components/Button";
import { Input } from "../components/Input";
import { Logo } from "../components/Logo";
import { useI18n } from "../lib/i18n";
import { useSession } from "../lib/session";
import { ApiError, auth } from "../lib/api";

function Login() {
  const { t } = useI18n();
  const { state, refresh } = useSession();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // If already signed in, send to /app immediately. Don't render the form.
  if (state === "signed") {
    void nav({ to: "/app", replace: true });
    return null;
  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setErr(null);
    setSubmitting(true);
    try {
      await auth.login({ email: email.trim().toLowerCase(), password });
      await refresh();
      void nav({ to: "/app", replace: true });
    } catch (e2) {
      if (e2 instanceof ApiError && e2.status === 401) {
        setErr(t("auth.login.badCredentials"));
      } else {
        setErr(t("common.errorGeneric"));
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <main className="min-h-full grid place-items-center px-4 py-12">
      <div className="w-full max-w-md flex flex-col gap-6">
        <div className="flex justify-center"><Logo size="lg" /></div>
        <Card>
          <h1 className="font-display text-2xl font-semibold mb-1">{t("auth.login.title")}</h1>
          <p className="text-secondary text-sm mb-6">{t("auth.login.subtitle")}</p>

          <form onSubmit={onSubmit} className="flex flex-col gap-4" noValidate>
            <Input
              label={t("auth.login.email")}
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            <Input
              label={t("auth.login.password")}
              type="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
            {err ? <p className="text-error text-sm" role="alert">{err}</p> : null}
            <Button type="submit" loading={submitting} full>{t("auth.login.submit")}</Button>
          </form>

          <div className="flex flex-col gap-3 mt-6 text-center text-sm">
            <Link to="/forgot-password" className="ring-nexus rounded text-secondary hover:text-primary underline-offset-4 hover:underline">
              {t("auth.login.forgot")}
            </Link>
            <Link to="/signup" className="ring-nexus rounded text-secondary hover:text-primary underline-offset-4 hover:underline">
              {t("auth.login.toSignup")}
            </Link>
          </div>
        </Card>
      </div>
    </main>
  );
}

export const Route = createFileRoute("/login")({
  component: Login,
});
